import { ImagePlus, Send } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import uuid from "react-native-uuid";
import { Spinner, Switch, View, XStack, YStack } from "tamagui";
import { useUploadMedia } from "../../../core/hooks/useUploadMedia";
import { Permission } from "../../../permissions/types";
import { Subtext } from "../../../ui/Subtext";
import type { BusinessPlanModel } from "../../plan/BusinessPlanModel";
import { BusinessFeedEventType } from "../BusinessFeedItem";
import type { FeedItemShoutOutData } from "../events/FeedItemShoutout";
import type { FeedItemTextData } from "../events/FeedItemText";
import { FeedFormImage } from "./FeedFormImage";
import { FeedTextInput } from "./FeedTextInput";

enum FeedInputType {
  Regular = "regular",
  ShoutOut = "shoutout",
}

export const FeedForm: React.FC<{ businessPlan: BusinessPlanModel }> = observer(
  ({ businessPlan }) => {
    const businessPlanUid = businessPlan.uid;
    const { t } = useTranslation();
    const [feedInputType, setFeedInputType] = useState(FeedInputType.Regular);
    const [_text, setText] = useState("");
    const inputRef = useRef<{ clear: () => void; focus: () => void }>(null);

    const getStoragePathSuffix = useCallback(async () => {
      return `${businessPlanUid}/${uuid.v4()}`;
    }, [businessPlanUid]);

    const { isUploading, imageSrc, selectFile, uploadFile, abort } =
      useUploadMedia(getStoragePathSuffix);

    const handleSubmit = useCallback(async () => {
      if (!businessPlan.userInPlan?.uid) {
        throw Error("User not found");
      }

      const text = _text.trim();

      if (!text && !imageSrc) {
        return;
      }

      let mediaUrl: string | null = null;

      if (imageSrc) {
        mediaUrl = await uploadFile();
      }

      switch (feedInputType) {
        case FeedInputType.Regular:
          businessPlan.addToFeed({
            type: BusinessFeedEventType.Text,
            moduleUid: "",
            itemData: {
              authorUid: businessPlan.userInPlan.uid,
              text: text,
              mediaUrl: mediaUrl ?? null,
            } satisfies FeedItemTextData,
          });
          break;
        case FeedInputType.ShoutOut:
          businessPlan.addToFeed({
            type: BusinessFeedEventType.GroupShoutOut,
            moduleUid: "",
            itemData: {
              authorUid: businessPlan.userInPlan.uid,
              text: text,
              mediaUrl,
            } satisfies FeedItemShoutOutData,
          });
          break;
      }

      // Reset to regular
      setFeedInputType(FeedInputType.Regular);
      inputRef.current?.clear();
    }, [
      _text,
      imageSrc,
      businessPlan.userInPlan?.uid,
      feedInputType,
      businessPlan.addToFeed,
      uploadFile,
    ]);

    const canUploadImage = !imageSrc;
    const borderRadius = 23;
    const submitDisabled = isUploading || (!imageSrc && !_text);

    return (
      <YStack>
        <View
          $xs={{
            paddingHorizontal: "$larger",
          }}
          zIndex={1}
        >
          <FeedTextInput
            businessPlan={businessPlan}
            placeholder={t("business:feed_share_text")}
            placeholderFocused={t("business:feed_mention_tip")}
            onSubmitEditing={handleSubmit}
            padding={"$medium"}
            paddingHorizontal={"$larger"}
            backgroundColor={"$backgroundBlock"}
            value={_text}
            onChangeText={(newText) => {
              setText(newText);
            }}
            ref={inputRef}
            borderRadius={borderRadius}
            $xs={{
              paddingHorizontal: "$medium",
            }}
          />
        </View>
        <View
          paddingTop={borderRadius}
          marginTop={-borderRadius}
          borderTopColor={"$divider"}
          borderTopWidth={1}
          backgroundColor={"$mutedBackground"}
        >
          {imageSrc && (
            <View
              backgroundColor={"$backgroundBlock"}
              borderBottomRightRadius={borderRadius}
              borderBottomLeftRadius={borderRadius}
              padding={"$space.medium"}
              paddingTop={borderRadius + 6}
              marginTop={-borderRadius}
              $xs={{
                marginHorizontal: "$larger",
              }}
            >
              <FeedFormImage onRemove={abort} imageSrc={imageSrc} />
            </View>
          )}
          <XStack
            justifyContent={canUploadImage ? "space-between" : "flex-end"}
            alignItems="center"
            height={50}
          >
            {canUploadImage && (
              <XStack
                paddingLeft={"$larger"}
                alignSelf="stretch"
                alignItems="center"
                gap="$small"
                onPress={selectFile}
                cursor="pointer"
              >
                <ImagePlus />{" "}
                <Subtext color="$color">{t("business:feed_media")}</Subtext>
              </XStack>
            )}

            <XStack justify="flex-end">
              {businessPlan.checkPermission(Permission.ShoutoutsVisible) && (
                <XStack
                  paddingRight={"$medium"}
                  alignSelf="stretch"
                  alignItems="center"
                  gap="$small"
                  onPress={() => {
                    setFeedInputType(
                      feedInputType === FeedInputType.ShoutOut
                        ? FeedInputType.Regular
                        : FeedInputType.ShoutOut,
                    );
                  }}
                  cursor="pointer"
                >
                  <Switch
                    padding={1}
                    borderWidth={1}
                    borderColor={"$divider"}
                    backgroundColor={"$backgroundBlock"}
                    size={"$medium"}
                    checked={feedInputType === FeedInputType.ShoutOut}
                    id={"offsetBeforeAfter"}
                    cursor="pointer"
                  >
                    <Switch.Thumb
                      size={"$medium"}
                      backgroundColor={
                        feedInputType === FeedInputType.ShoutOut
                          ? "$pressablePrimaryBackground"
                          : "#ccc"
                      }
                      animation="quicker"
                      alignItems="center"
                      justifyContent="center"
                    />
                  </Switch>

                  <Subtext color="$color">
                    {t("business:feed_shoutout")}
                  </Subtext>
                </XStack>
              )}
              <XStack
                onPress={handleSubmit}
                cursor={submitDisabled ? "default" : "pointer"}
                opacity={submitDisabled ? 0.25 : 1}
                alignSelf="stretch"
                alignItems="center"
                gap="$small"
                disabled={submitDisabled}
                paddingLeft={"$medium"}
                paddingRight={"$larger"}
              >
                {isUploading ? <Spinner /> : <Send />}
                <Subtext color="$color">{t("business:feed_post")}</Subtext>
              </XStack>
            </XStack>
          </XStack>
        </View>
      </YStack>
    );
  },
);
